import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const RecentTransactionsTable = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('all');

  const transactions = [
    {
      id: "TXN-2024-001234",
      recipient: "Maria Rodriguez",
      recipientBank: "Banco Santander Spain",
      amount: "€1,250.00",
      currency: "EUR",
      status: "completed",
      date: "2024-11-18",
      time: "09:30",
      reference: "Family Support",
      exchangeRate: "1.0850"
    },
    {
      id: "TXN-2024-001233",
      recipient: "John Smith",
      recipientBank: "HSBC UK",
      amount: "£850.00",
      currency: "GBP",
      status: "processing",
      date: "2024-11-17",
      time: "14:15",
      reference: "Business Payment",
      exchangeRate: "1.2650"
    },
    {
      id: "TXN-2024-001232",
      recipient: "Ahmed Hassan",
      recipientBank: "Emirates NBD",
      amount: "AED 2,500.00",
      currency: "AED",
      status: "pending_review",
      date: "2024-11-16",
      time: "11:45",
      reference: "Investment Transfer",
      exchangeRate: "3.6730"
    },
    {
      id: "TXN-2024-001231",
      recipient: "Lisa Chen",
      recipientBank: "Bank of China",
      amount: "¥8,500.00",
      currency: "CNY",
      status: "failed",
      date: "2024-11-15",
      time: "16:20",
      reference: "Education Fee",
      exchangeRate: "7.2450"
    },
    {
      id: "TXN-2024-001230",
      recipient: "Pierre Dubois",
      recipientBank: "BNP Paribas",
      amount: "€2,100.00",
      currency: "EUR",
      status: "completed",
      date: "2024-11-14",
      time: "10:30",
      reference: "Property Purchase",
      exchangeRate: "1.0850"
    }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'completed', label: 'Completed' },
    { value: 'processing', label: 'Processing' },
    { value: 'pending_review', label: 'Pending Review' },
    { value: 'failed', label: 'Failed' }
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { color: 'text-success bg-success/10', icon: 'CheckCircle', label: 'Completed' },
      processing: { color: 'text-warning bg-warning/10', icon: 'Clock', label: 'Processing' },
      pending_review: { color: 'text-accent bg-accent/10', icon: 'AlertCircle', label: 'Pending Review' },
      failed: { color: 'text-error bg-error/10', icon: 'XCircle', label: 'Failed' }
    };

    const config = statusConfig?.[status] || statusConfig?.processing;
    
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        <Icon name={config?.icon} size={12} />
        <span>{config?.label}</span>
      </span>
    );
  };

  const filteredTransactions = statusFilter === 'all' 
    ? transactions 
    : transactions?.filter(tx => tx?.status === statusFilter);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <h2 className="text-lg font-semibold text-foreground mb-4 sm:mb-0">Recent Transactions</h2>
        <div className="flex items-center space-x-4">
          <Select
            options={statusOptions}
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="Filter by status"
            className="w-40"
          />
          <Button
            variant="outline"
            size="sm"
            iconName="ExternalLink"
            iconPosition="right"
            onClick={() => navigate('/transaction-history')}
          >
            View All
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Transaction</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Recipient</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Amount</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransactions?.map((transaction) => (
              <tr key={transaction?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                <td className="py-4 px-4">
                  <div>
                    <p className="text-sm font-medium text-foreground">{transaction?.id}</p>
                    <p className="text-xs text-muted-foreground">{transaction?.reference}</p>
                  </div>
                </td>
                <td className="py-4 px-4">
                  <div>
                    <p className="text-sm font-medium text-foreground">{transaction?.recipient}</p>
                    <p className="text-xs text-muted-foreground">{transaction?.recipientBank}</p>
                  </div>
                </td>
                <td className="py-4 px-4">
                  <div>
                    <p className="text-sm font-medium text-foreground">{transaction?.amount}</p>
                    <p className="text-xs text-muted-foreground">Rate: {transaction?.exchangeRate}</p>
                  </div>
                </td>
                <td className="py-4 px-4">
                  {getStatusBadge(transaction?.status)}
                </td>
                <td className="py-4 px-4">
                  <div>
                    <p className="text-sm text-foreground">{transaction?.date}</p>
                    <p className="text-xs text-muted-foreground">{transaction?.time}</p>
                  </div>
                </td>
                <td className="py-4 px-4 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Eye"
                    onClick={() => navigate(`/transaction-history?id=${transaction?.id}`)}
                  >
                    View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filteredTransactions?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="FileX" size={48} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No transactions found for the selected filter.</p>
        </div>
      )}
    </div>
  );
};

export default RecentTransactionsTable;